import { useCallback } from 'react';

import { CapacitorException, ExceptionCode } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import * as Sentry from '@sentry/react';

export type HapticStrength = 'light' | 'medium' | 'heavy';

const STYLE_OF: Record<HapticStrength, ImpactStyle> = {
  light: ImpactStyle.Light,
  medium: ImpactStyle.Medium,
  heavy: ImpactStyle.Heavy,
};

const isUnavailable = (err: unknown) =>
  err instanceof CapacitorException && err.code === ExceptionCode.Unavailable;

export interface UseHapticsOptions {
  /** Off, nothing is felt. */
  isEnabled?: boolean;
}

/**
 * useHaptics gives a tap the device can feel, natively or through the
 * browser's vibrate where there is one.
 */
export const useHaptics = ({ isEnabled = true }: UseHapticsOptions = {}) => {
  const impact = useCallback(
    (strength: HapticStrength = 'light') => {
      if (!isEnabled) return;
      Haptics.impact({ style: STYLE_OF[strength] }).catch((err: unknown) => {
        // Expected: browsers without vibrate (iOS Safari, desktop) cannot be felt
        if (isUnavailable(err)) return;
        Sentry.captureException(err, { tags: { 'haptics.action': 'impact' } });
      });
    },
    [isEnabled],
  );

  return { impact };
};
